import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { MenuItem, Tooltip } from "@mui/material";
import { Translate } from "orion-components/i18n";
import CustomizedTextField from "orion-components/SharedComponents/CustomizedTextField/CustomizedTextField";
import { alertService } from "client-app-core";

const alarmSummaryStyles = {
	container: {
		display: "flex",
		flexDirection: "column",
		padding: "16px",
		backgroundColor: "#2C2D2F",
		color: "#fff"
	},
	widgetTitle: {
		fontFamily: "Roboto",
		fontStyle: "normal",
		fontWeight: "normal",
		fontSize: "12px",
		lineHeight: "16px",
		letterSpacing: "0px",
		color: "#FFFFFF",
		opacity: 1
	},
	formRow: {
		display: "grid",
		gridTemplateColumns: "repeat(2, 155px)",
		columnGap: "20px",
		rowGap: "20px",
		justifyContent: "flex-start"
	},
	field: {
		marginTop: "10px",
		display: "flex",
		flexDirection: "column",
		minWidth: "155px",
		gap: "2px"
	},
	label: {
		fontSize: "9px",
		marginBottom: "4px",
		color: "#8D8D8D",
		lineHeight: "1"
	}
};

const AlarmSummaryWidget = forwardRef((props, ref) => {
	const { alert, disabled, readOnly, forReplay } = props;
	const isDownloadedReplay = typeof window !== "undefined" && Boolean(window.api);
	const isReadOnlyDownloadedReplay = forReplay && isDownloadedReplay;
	const isFieldDisabled = disabled || readOnly || isReadOnlyDownloadedReplay;
	const [selectedSummaryCodes, setSelectedSummaryCodes] = useState({});
	const [summaryCodeErrors, setSummaryCodeErrors] = useState({});
	const [summaryCodes, setSummaryCodes] = useState([]);
	const availableSummaryCodes = summaryCodes;

	useImperativeHandle(ref, () => ({
		getSummaryCodes: () => availableSummaryCodes,
		setSummaryCodeErrors: errors => setSummaryCodeErrors(errors),
		setSelectedSummaryCodes: codes => setSelectedSummaryCodes(codes),
		getSelectedSummaryCodes: () => selectedSummaryCodes
	}));

	useEffect(() => {
		if (isReadOnlyDownloadedReplay) {
			const offlineSummaryCodes = window.api.getState("alertSummaryCodes");
			if (Array.isArray(offlineSummaryCodes) && offlineSummaryCodes.length > 0) {
				const sorted = [...offlineSummaryCodes].sort((a, b) => {
					const aOrder = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
					const bOrder = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
					return aOrder - bOrder;
				});
				setSummaryCodes(sorted);
			}
			return;
		}

		alertService.getAlertSummaryCodes((err, response) => {
			if (!err && response) {
				const sorted = [...response].sort((a, b) => {
					// Items with no 'order' should come last
					const aOrder = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
					const bOrder = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
					return aOrder - bOrder;
				});
				setSummaryCodes(sorted);
			} else {
				console.error("Failed to load summary codes", err);
			}
		});
	}, [isReadOnlyDownloadedReplay]);

	if (!availableSummaryCodes?.length) return null;

	const handleSummaryChange = (id, value) => {
		if (isFieldDisabled) return;
		const data = { ...selectedSummaryCodes };

		data[id] = value;
		setSelectedSummaryCodes(data);

		// Clear any errors that were resolved by this change
		const updatedErrors = { ...summaryCodeErrors };
		Object.keys(updatedErrors).forEach(errorId => {
			const code = summaryCodes.find(c => c.id === errorId);
			if (code && code.required && data[errorId]) {
				delete updatedErrors[errorId];
			}
		});
		setSummaryCodeErrors(updatedErrors);

		//  Save the updated summary code (no validation here)
		const summaryPayload = { alertSummary: data };
		alertService.setAlertSummaryCodes(alert.id, summaryPayload, (err, response) => {
			if (err) {
				console.error(`Failed to save summary code for ${id}`, err);
			}
		});
	};

	return (
		<div style={alarmSummaryStyles.container}>
			<div style={alarmSummaryStyles.widgetTitle}>
				<Translate value="global.profiles.widgets.alarmDetail.alarmSummary" />
			</div>
			<div style={alarmSummaryStyles.formRow}>
				{availableSummaryCodes.map((code, index) => (
					<div key={code.id} style={alarmSummaryStyles.field}>
						<div style={alarmSummaryStyles.label}>{code.label}</div>
						<Tooltip
							title={
								disabled ? (
									<Translate value="global.profiles.widgets.alarmDetail.alarmSummaryLocked" />
								) : (
									""
								)
							}
							arrow
							disableHoverListener={!disabled}
						>
							<div style={{ display: "inline-block" }}>
								{isReadOnlyDownloadedReplay ? (
									<CustomizedTextField
										id={`alarm-summary-${index}`}
										customWidth={155}
										variant="outlined"
										value={selectedSummaryCodes[code.id] || ""}
										disabled={true}
										sx={{
											"& .MuiOutlinedInput-root": {
												backgroundColor: "#292B30",
												color: "#fff",
												borderRadius: "6px",
												"& fieldset": {
													borderColor: "#444"
												},
												"&.Mui-disabled": {
													backgroundColor: "#292B30",
													color: "#fff",
													opacity: 1
												},
												"&.Mui-disabled fieldset": {
													borderColor: "#444"
												}
											},
											"& .MuiInputBase-input.Mui-disabled": {
												WebkitTextFillColor: "#fff"
											}
										}}
									/>
								) : (
									<CustomizedTextField
										id={`alarm-summary-${index}`}
										customWidth={155}
										variant="outlined"
										value={selectedSummaryCodes[code.id] || ""}
										select
										disabled={isFieldDisabled}
										onChange={e => handleSummaryChange(code.id, e.target.value)}
										error={!!summaryCodeErrors[code.id]}
										helperText={
											summaryCodeErrors[code.id] && (
												<Translate value="global.profiles.widgets.alarmDetail.required" />
											)
										}
										SelectProps={{
											MenuProps: {
												PaperProps: {
													sx: {
														backgroundColor: "#292B30",
														color: "#fff",
														boxShadow: "none"
													}
												}
											}
										}}
										sx={{
											"& .MuiOutlinedInput-root": {
												backgroundColor: "#292B30",
												color: "#fff",
												borderRadius: "6px",
												"& fieldset": {
													borderColor: "#444"
												},
												"&:hover fieldset": {
													borderColor: "#666"
												},
												"&.Mui-focused fieldset": {
													borderColor: "#888"
												},

												"&.Mui-disabled": {
													backgroundColor: "#292B30",
													color: "#fff",
													opacity: 1
												},

												"&.Mui-disabled fieldset": {
													borderColor: "#444"
												}
											},

											"& .MuiInputBase-input.Mui-disabled": {
												WebkitTextFillColor: "#fff"
											},

											"& .MuiSelect-icon": {
												color: "#fff"
											},

											"& .MuiSelect-icon.Mui-disabled": {
												color: "#fff"
											}
										}}
									>
										{!code.required && (
											<MenuItem
												value=""
												sx={{
													backgroundColor: "#292B30",
													color: "#fff",
													fontSize: "10px",
													fontStyle: "italic",
													"&:hover": {
														backgroundColor: "#2F333B"
													}
												}}
											>
												<Translate value="global.profiles.widgets.alarmDetail.none" />
											</MenuItem>
										)}
										{(code.options || []).map(option => (
											<MenuItem
												key={option}
												value={option}
												sx={{
													backgroundColor: "#292B30",
													color: "#fff",
													fontSize: "10px",
													"&:hover": {
														backgroundColor: "#2F333B"
													}
												}}
											>
												{option}
											</MenuItem>
										))}
									</CustomizedTextField>
								)}
							</div>
						</Tooltip>
					</div>
				))}
			</div>
		</div>
	);
});

export default AlarmSummaryWidget;
